import React, { useState, useEffect } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { List, ListItem, Divider, IconButton, Tooltip, Text, Flex, Box, Icon } from '@vibe/core';
import { MoveArrowDown, Workflow, Drag, Hide } from '@vibe/icons';
import { type Page } from '@gorilla/portal-shared/src/schemas';

type PageListProps = {
  pages: Page[];
  selectedPageId: string | null;
  togglePortalPages: boolean;
  screenMode: 'small' | 'medium' | 'large';
  onPageSelect: (pageId: string) => void;
  onPageReorder: (pages: Page[]) => void;
  onDeleteClick: (pageId: string) => void;
};

type SortablePageItemProps = {
  page: Page;
  isSelected: boolean;
  isWideMode: boolean;
  screenMode: 'small' | 'medium' | 'large';
  disabled: boolean;
  onSelect: () => void;
  onDeleteClick: (pageId: string) => void;
};

function SortablePageItem({ page, isSelected, isWideMode, screenMode, disabled, onSelect, onDeleteClick }: SortablePageItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: page.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : undefined,
  };

  if (isWideMode) {
    return (
      <div ref={setNodeRef} style={style}>
        <ListItem
          className="portalmanager__content__listitem portalmanager__content__listitem--pages"
          size="medium"
          selected={isSelected}
          onClick={onSelect}
        >
          {/* Page icon */}
          <div
            style={{
              width: '18px',
              height: '18px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '10px',
            }}
          >
            {page.icon ? <i className={`fa-regular fa-${page.icon}`}></i> : <i className={`fa-regular fa-file`}></i>}
          </div>

          {/* Page name and badges */}
          <Flex gap={8}>
            <div
              data-menu={page.menu ?? undefined}
              data-conditions={page.conditions && page.conditions.length > 0 ? true : undefined}
              data-screen-mode={screenMode ?? undefined}
              style={{
                pointerEvents: 'none',
                userSelect: 'none',
              }}
            >
              <Text
                type="text2"
                align="center"
                color="primary"
                ellipsis
                maxLines={1}
                withoutTooltip
                className="portalmanager__content__listitem--pagetitle"
              >
                {page.name}
              </Text>
            </div>
            <Flex gap={0}>
              {page.menu === 'footer' ? (
                <Tooltip content="Page displayed in footer" position="right" showOnDialogEnter={false}>
                  <IconButton
                    icon={MoveArrowDown}
                    kind="tertiary"
                    size="xs"
                    disabled
                    onClick={() => {}}
                    className="custom__iconbutton--xs"
                  />
                </Tooltip>
              ) : null}
              {page.conditions && page.conditions.length > 0 ? (
                <Tooltip content="Page displayed conditionally" position="right" showOnDialogEnter={false}>
                  <IconButton icon={Workflow} kind="tertiary" size="xs" disabled onClick={() => {}} className="custom__iconbutton--xs" />
                </Tooltip>
              ) : null}

              {page.hidden ? (
                <Tooltip content="Page not displayed" position="right" showOnDialogEnter={false}>
                  <IconButton icon={Hide} kind="tertiary" size="xs" disabled onClick={() => {}} className="custom__iconbutton--xs" />
                </Tooltip>
              ) : null}
            </Flex>
          </Flex>

          <Box className="portalmanager__content__listitem--actions" style={{ marginLeft: 'auto' }}>
            <Flex>
              {/* Drag handle */}
              <div
                {...attributes}
                {...listeners}
                style={{
                  cursor: disabled ? 'default' : isDragging ? 'grabbing' : 'grab',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '24px',
                  height: '24px',
                }}
              >
                <Icon icon={Drag} size={18} />
              </div>
            </Flex>
          </Box>
        </ListItem>
      </div>
    );
  }

  // Narrow/collapsed mode - drag via handle only
  return (
    <div>
      <ListItem
        className="portalmanager__content__listitem portalmanager__content__listitem--center portalmanager__content__listitem--pages"
        size="medium"
        selected={isSelected}
        onClick={onSelect}
      >
        <Flex direction="column" align="center" gap={4}>
          <Tooltip content={page.name} position="right" showOnDialogEnter={false}>
            <div
              style={{
                width: '18px',
                height: '18px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {page.icon ? <i className={`fa-regular fa-${page.icon}`}></i> : <i className={`fa-regular fa-square-dashed`}></i>}
            </div>
          </Tooltip>
          {/* Drag handle */}
          {/* 
          <div
            {...attributes}
            {...listeners}
            style={{
              cursor: disabled ? 'default' : 'grab',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '18px',
              height: '18px',
            }}
          >
            <Icon icon={Drag} iconSize={14} />
          </div>
          */}
        </Flex>
      </ListItem>
    </div>
  );
}

export function PageList({
  pages,
  selectedPageId,
  togglePortalPages,
  screenMode,
  onPageSelect,
  onPageReorder,
  onDeleteClick,
}: PageListProps) {
  // Local state to prevent flicker on drag end
  const [localPages, setLocalPages] = useState(pages);

  // Sync local state with props when pages change from parent
  useEffect(() => {
    setLocalPages(pages);
  }, [pages]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localPages.findIndex((p) => p.id === active.id);
      const newIndex = localPages.findIndex((p) => p.id === over.id);
      const newPages = arrayMove(localPages, oldIndex, newIndex);

      // Update local state immediately to prevent flicker
      setLocalPages(newPages);
      // Propagate change to parent
      onPageReorder(newPages);
    }
  }

  const isWideMode = togglePortalPages || screenMode !== 'small';

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={localPages.map((p) => p.id)} strategy={verticalListSortingStrategy}>
        <List style={{ overflow: 'hidden' }}>
          {localPages.map((page) => (
            <React.Fragment key={page.id}>
              <SortablePageItem
                page={page}
                isSelected={page.id === selectedPageId}
                isWideMode={isWideMode}
                screenMode={screenMode}
                disabled={localPages.length <= 1}
                onSelect={() => onPageSelect(page.id)}
                onDeleteClick={() => onDeleteClick(page.id)}
              />
              <Divider withoutMargin />
            </React.Fragment>
          ))}
        </List>
      </SortableContext>
    </DndContext>
  );
}
